import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect, useLayoutEffect, } from "react";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import styled from "@emotion/styled";
import { Fragment } from "react";
import { UI } from "../icons";
import { useElementsContext } from "../elementsProvider";
import { colors } from "../theme";
function UrlWrapper({ href, children }) {
    if (href !== "") {
        return (_jsx(Link, Object.assign({ target: "_blank", href: href }, { children: children }), void 0));
    }
    return children;
}
export function RTCImageCarrousel({ layerColor, size, pageIndicatorColor, skin = "1", subelements, }) {
    const { isMobile, context } = useElementsContext();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [pause, setPause] = useState(false);
    const [disableClick, setDisableClick] = useState(false);
    const timer = useRef(null);
    const [sliderRef, slider] = useKeenSlider({
        duration: context === "editor" ? 0 : 800,
        initial: 0,
        loop: true,
        dragStart: () => {
            if (isMobile) {
                setPause(true);
            }
            else {
                setTimeout(() => {
                    setPause(true);
                    setDisableClick(true);
                }, 100);
            }
        },
        dragEnd: () => {
            if (isMobile) {
                setPause(false);
            }
            else {
                setTimeout(() => {
                    setPause(false);
                    setDisableClick(false);
                }, 100);
            }
        },
        slideChanged(s) {
            setCurrentSlide(s.details().relativeSlide);
        },
    });
    const sliderSize = subelements.length;
    useLayoutEffect(() => {
        // keen-slider library doesn't resizes correctly because of the previewer animation. This is a tested solution.
        setTimeout(() => {
            slider === null || slider === void 0 ? void 0 : slider.resize();
        }, 430);
    }, [subelements.length, slider, isMobile]);
    useEffect(() => {
        const slider = sliderRef === null || sliderRef === void 0 ? void 0 : sliderRef.current;
        const pause = () => setPause(true);
        const play = () => setPause(false);
        slider === null || slider === void 0 ? void 0 : slider.addEventListener("mouseover", pause);
        slider === null || slider === void 0 ? void 0 : slider.addEventListener("mouseout", play);
        return () => {
            slider === null || slider === void 0 ? void 0 : slider.removeEventListener("mouseover", pause);
            slider === null || slider === void 0 ? void 0 : slider.removeEventListener("mouseout", play);
        };
    }, [sliderRef]);
    useEffect(() => {
        timer.current = setInterval(() => {
            if (!pause && slider && sliderSize) {
                slider.next();
            }
        }, 5000);
        return () => {
            timer.current && clearInterval(timer.current);
        };
    }, [pause, slider, sliderSize]);
    return (_jsx(SliderContainer, { children: _jsxs(SliderWrapper, Object.assign({ className: "navigation-wrapper" }, { children: [_jsx(Slider, Object.assign({ className: sliderSize > 1 ? "keen-slider" : "", size: size, ref: sliderRef, style: { display: "flex", overflow: "hidden" } }, { children: subelements.map(subelement => {
                        const images = JSON.parse(subelement.imageUrl);
                        return (_jsx(Slide, Object.assign({ className: sliderSize > 1 ? "keen-slider__slide" : "" }, { children: _jsx(UrlWrapper, Object.assign({ href: disableClick ? "" : subelement.url }, { children: _jsxs(Fragment, { children: [layerColor !== "" && (_jsx(Layer, { layerColor: layerColor, skin: skin }, void 0)),
                                        (skin === "1" || skin === "3") && (_jsx(OpacityLayer, { opacity: subelement.opacity }, void 0)),
                                        _jsx(Images, { alt: "imagen1", skin: skin, src: isMobile ? images.medium : images.full }, subelement.identifier),
                                        subelement.text && (_jsx(TextContainer, Object.assign({ skin: skin, isDesktop: !isMobile }, { children: _jsx(Text, Object.assign({ size: size, skin: skin, textColor: subelement.textColor, isDesktop: !isMobile }, { children: subelement.text }), void 0) }), void 0))] }, void 0) }), void 0) }), subelement.identifier));
                    }) }), void 0),
                slider && sliderSize > 1 && (skin === "3" || skin === "4") && (_jsxs(Fragment, { children: [_jsx(ArrowLeft, { onClick: e => {
                                e.stopPropagation();
                                slider.prev();
                            }, isDesktop: !isMobile }, void 0),
                        _jsx(ArrowRight, { onClick: e => {
                                e.stopPropagation();
                                slider.next();
                            }, isDesktop: !isMobile }, void 0)] }, void 0)),
                slider && (skin === "1" || skin === "2") && sliderSize > 1 && (_jsx(PageIndicatorContainer, { children: Array.from(Array(sliderSize).keys()).map(index => (_jsx(PageIndicator, { "aria-label": `${index + 1}`, index: index, isSelected: currentSlide === index, onClick: () => {
                            slider.moveToSlideRelative(index);
                        }, onKeyDown: () => {
                            slider.moveToSlideRelative(index);
                        }, pageIndicatorColor: pageIndicatorColor, role: "button", isDesktop: !isMobile }, index))) }, void 0))] }), void 0) }, void 0));
}
function ArrowLeft({ onClick, isDesktop }) {
    return (_jsx(Chevron, Object.assign({ className: "arrow arrow--left", next: false, onClick: onClick, prev: true, isDesktop: isDesktop }, { children: _jsx(UI.Chevron, {}, void 0) }), void 0));
}
function ArrowRight({ onClick, isDesktop }) {
    return (_jsx(Chevron, Object.assign({ className: "arrow arrow--right", next: true, onClick: onClick, prev: false, isDesktop: isDesktop }, { children: _jsx(UI.Chevron, {}, void 0) }), void 0));
}
const SliderContainer = styled.section(() => ({
    width: "100%",
}));
const SliderWrapper = styled.div(() => ({
    position: "relative",
}));
const Slider = styled.div(({ size }) => {
    let aspecRatioDesktop;
    switch (size) {
        case "medium":
            // medium
            aspecRatioDesktop = "4.8/1";
            break;
        case "small":
            // small
            aspecRatioDesktop = "9.6/1";
            break;
        default:
            // big
            aspecRatioDesktop = "3.2/1";
            break;
    }
    return {
        aspectRatio: aspecRatioDesktop,
    };
});
const Slide = styled.article(() => ({
    height: "100%",
    width: "100%",
}));
const Layer = styled.div(({ layerColor, skin }) => {
    let layerColorSetup;
    switch (skin) {
        case "2":
            layerColorSetup = layerColor;
            break;
        case "3":
            layerColorSetup = "";
            break;
        case "4":
            layerColorSetup = layerColor;
            break;
        default:
            // skin 1
            layerColorSetup = "";
            break;
    }
    return {
        backgroundColor: layerColorSetup,
        clipPath: "polygon(17% 0%,100% 0%,100% 100%,0% 100%)",
        height: "100%",
        position: "absolute",
        right: 0,
        top: 0,
        width: "63%",
        zIndex: 1,
    };
});
const OpacityLayer = styled.div(({ opacity }) => ({
    backgroundColor: colors.black,
    height: "100%",
    opacity: opacity,
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    zIndex: 1,
}));
const Images = styled.img(({ skin }) => ({
    height: "100%",
    objectFit: "cover",
    width: skin === "2" || skin === "4" ? "50%" : "100%",
}));
const Link = styled.a(() => ({
    cursor: "pointer",
    display: "inherit",
    height: "100%",
    width: "100%",
}));
const TextContainer = styled.div(({ skin, isDesktop }) => {
    let height;
    let maxWidthDesktop;
    let maxWidthMobile;
    let padding;
    let positionLeft;
    let textAlign;
    switch (skin) {
        case "2":
            height = "100%";
            maxWidthDesktop = "40rem";
            maxWidthMobile = "10.8rem";
            padding = "";
            positionLeft = "55%";
            textAlign = "left";
            break;
        case "3":
            height = "100%";
            maxWidthDesktop = "";
            maxWidthMobile = "";
            padding = "0 1.6rem";
            positionLeft = "unset";
            textAlign = "center";
            break;
        case "4":
            height = "100%";
            maxWidthDesktop = "40rem";
            maxWidthMobile = "10.8rem";
            padding = "";
            positionLeft = "55%";
            textAlign = "left";
            break;
        default:
            // skin 1
            height = "90%";
            maxWidthDesktop = "";
            maxWidthMobile = "";
            padding = "0 1.6rem";
            positionLeft = "unset";
            textAlign = "center";
            break;
    }
    return Object.assign({ alignItems: "center", display: "flex", flexDirection: "row", height, justifyContent: "center", left: positionLeft, maxWidth: maxWidthMobile, padding: padding, position: "absolute", textAlign: textAlign, top: 0, width: "100%", zIndex: 1 }, (isDesktop
        ? {
            maxWidth: maxWidthDesktop,
        }
        : {}));
});
const Text = styled.h2(({ size, textColor, skin, isDesktop }) => {
    let fontSizeDesktop;
    let fontSizeMobile;
    let skinEven = skin === "2" || skin === "4";
    switch (size) {
        case "medium":
            // medium
            fontSizeDesktop = skinEven ? "3.6rem" : "6rem";
            fontSizeMobile = skinEven ? "1.6rem" : "2.4rem";
            break;
        case "small":
            // small
            fontSizeDesktop = skinEven ? "3rem" : "5rem";
            fontSizeMobile = skinEven ? "1.6rem" : "2rem";
            break;
        default:
            // big
            fontSizeDesktop = skinEven ? "4rem" : "6rem";
            fontSizeMobile = skinEven ? "1.8rem" : "2.4rem";
            break;
    }
    return Object.assign({ color: textColor, fontFamily: "inherit", fontSize: fontSizeMobile, fontWeight: 500, margin: 0, padding: "0 1.2rem", wordBreak: "break-word" }, (isDesktop
        ? {
            fontSize: fontSizeDesktop,
        }
        : {}));
});
const Chevron = styled.button(({ next, prev, isDesktop }) => ({
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    height: "100%",
    left: prev ? 0 : "",
    outline: "none",
    padding: "0 0.8rem",
    position: "absolute",
    right: next ? 0 : "",
    top: 0,
    zIndex: 1,
    "& svg": Object.assign({ color: colors.white, fontSize: "1.6rem", filter: "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.2))", transform: prev ? "rotate(90deg)" : "rotate(270deg)" }, (isDesktop
        ? {
            fontSize: "2.4rem",
        }
        : {})),
}));
const PageIndicatorContainer = styled.div(() => ({
    bottom: 0,
    position: "absolute",
    textAlign: "center",
    width: "100%",
}));
const PageIndicator = styled.li(({ isSelected, pageIndicatorColor, isDesktop }) => (Object.assign({ backgroundColor: isSelected ? pageIndicatorColor : colors.greyMedium, border: `solid 1px ${colors.white}`, borderRadius: "50%", cursor: "pointer", display: "inline-block", height: "0.8rem", margin: " 0.8rem 0.4rem", outline: "none", width: "0.8rem", zIndex: 1 }, (isDesktop
    ? {
        height: "1.2rem",
        margin: "1.6rem 0.8rem",
        width: "1.2rem",
    }
    : {}))));
//# sourceMappingURL=RTCImageCarrousel.js.map